// Persists a pending co-carer invite URL across the auth flow
// (register → email verification → login), since Firebase's verification
// email is sent to a static action URL and continueUrl params get lost
// when the user re-enters via a fresh tab/login screen.

const KEY = 'kc_pending_invite_url';

/** Returns true if the URL looks like an auth-action acceptpetinvite link. */
function isInviteUrl(url: string): boolean {
  try {
    const u = new URL(url, window.location.origin);
    // The app router uses /auth/action; tolerate /auth-action too in case
    // older builds, redirects or shortened links use the dashed form.
    const isAuthActionPath =
      u.pathname === '/auth/action' ||
      u.pathname.startsWith('/auth/action/') ||
      u.pathname === '/auth-action' ||
      u.pathname.startsWith('/auth-action/');
    if (!isAuthActionPath) return false;
    return (u.searchParams.get('mode') ?? '').toLowerCase() === 'acceptpetinvite';
  } catch {
    return false;
  }
}

export function setPendingInvite(url: string): void {
  try {
    if (!isInviteUrl(url)) return;
    localStorage.setItem(KEY, url);
  } catch {
    /* ignore quota / privacy errors */
  }
}

export function getPendingInvite(): string | null {
  try {
    const v = localStorage.getItem(KEY);
    if (!v || !isInviteUrl(v)) return null;
    return v;
  } catch {
    return null;
  }
}

export function clearPendingInvite(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Normalize any URL or path into a router-safe relative path
 * (`pathname + search + hash`). Returns `null` if the URL is
 * malformed or points at an external origin we don't trust.
 */
function toRouterPath(raw: string): string | null {
  try {
    const u = new URL(raw, window.location.origin);
    const allowedHosts = new Set([
      window.location.hostname,
      'kodacare.pet',
      'www.kodacare.pet',
      'app.kodacare.pet',
    ]);
    if (!allowedHosts.has(u.hostname)) return null;
    const path = `${u.pathname}${u.search}${u.hash}`;
    return path || '/';
  } catch {
    return null;
  }
}

/**
 * Resolve the post-auth destination. Prefers an explicit continueUrl param,
 * then falls back to a stored pending invite, then a default path.
 * Always returns a router-relative path (never an absolute URL), so it is
 * safe to pass directly to `navigate()`.
 * Does NOT clear the pending invite — the AuthAction page does that on
 * successful processing so retries are still possible if the flow fails.
 */
export function resolvePostAuthDestination(continueUrl: string | null, fallback = '/splash'): string {
  if (continueUrl) {
    let decoded = continueUrl;
    try { decoded = decodeURIComponent(continueUrl); } catch { /* keep raw */ }
    const path = toRouterPath(decoded);
    if (path) return path;
  }
  const pending = getPendingInvite();
  if (pending) {
    const path = toRouterPath(pending);
    if (path) return path;
  }
  return fallback;
}
